// ============================================================
// MeldRadar — Intelligence detail
// ============================================================
// Everything known about one report: how sure we are it is real, how long
// these tend to last here, when they happen, and how often a comparable
// window has held one.
//
// The backend returns null for anything it cannot justify, and this screen
// honours that literally: a missing value means the block is not rendered.
// Showing "onbekend" would still occupy the reader's attention; showing a
// plausible number would be worse. Nothing here is filled in on the client.

import { dagMaand, uurMinuut } from "./maanden";
import { vrijgegevenLabel } from "./leeftijd";
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import "./IntelligenceDetail.css";

// Same fallback as App.js and MiniApp.js. Without it, an unset
// REACT_APP_BACKEND_URL is inlined as the string "undefined" at build time and
// every request from this screen goes to /undefined/api/... — a 404 that looks
// like a broken feature rather than a missing setting, and only on this screen,
// because the two other entry points do fall back.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const WINDOWS = ["24u", "7d", "30d", "90d"];
const DAY_LABELS = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

// Six steps, dark to bright. The lowest step is the empty-cell colour, so a
// quiet week reads as quiet rather than as missing data.
const HEAT_STEPS = ["#131b23", "#14532d", "#166534", "#16a34a", "#4ade80", "#86efac"];

// Three-letter marks, because a driver recognises the service faster than the
// word. Anything unlisted falls back to the first letters of its name.
const MARKS = {
  politie: { text: "POL", tone: "pol" },
  ilt: { text: "ILT", tone: "ilt" },
  belasting: { text: "BEL", tone: "bel" },
  douane: { text: "DOU", tone: "dou" },
  marechaussee: { text: "KMAR", tone: "kmar" },
  taxiteam: { text: "TAXI", tone: "taxi" },
  vaste_flitser: { text: "FLIT", tone: "flits" },
  trajectcontrole: { text: "TRAJ", tone: "flits" },
  roodlichtcamera: { text: "ROOD", tone: "flits" },
  mobiele_flitser: { text: "FLIT", tone: "flits" },
  wegwerkzaamheden: { text: "WERK", tone: "werk" },
  // Situaties en afsluitingen. Ze stonden hier niet, en zolang ze alleen uit
  // een enkele gebruikersmelding kwamen viel dat niet op: een onbekend woord
  // valt terug op de eerste vier letters in politieblauw. Sinds de
  // gebeurtenissenlaag ze op het bord zet — ongeval, pech, object op de weg,
  // brug open — zijn dat de meeste rijen, en dan is blauw de verkeerde kleur:
  // dat is de kleur van iemand die staat te controleren.
  ongeval: { text: "ONGE", tone: "sit" },
  kapot_voertuig: { text: "PECH", tone: "sit" },
  object_op_weg: { text: "OBJ", tone: "sit" },
  gevaar: { text: "LET", tone: "sit" },
  wegafsluiting: { text: "DICHT", tone: "werk" },
  rijstrook_dicht: { text: "RIJS", tone: "werk" },
  brug_open: { text: "BRUG", tone: "werk" },
};

export function authorityMark(authority, name) {
  return MARKS[authority] || { text: (name || "?").slice(0, 4).toUpperCase(), tone: "pol" };
}

/** Control types that say nothing, so the service name is all there is to say. */
const GENERIC_CONTROL = new Set(["Algemeen", "Onbekend", ""]);

const COMPASS = ["N", "NO", "O", "ZO", "Z", "ZW", "W", "NW"];

/** Degrees to a Dutch compass point. 0 is north, and it wraps. */
export function compassPoint(degrees) {
  if (degrees == null || Number.isNaN(degrees)) return null;
  return COMPASS[Math.round((((degrees % 360) + 360) % 360) / 45) % 8];
}

/**
 * The posted limit, as a number, or null.
 *
 * OSM's maxspeed is free text: mostly "100", sometimes "RO:urban" or "walk".
 * Only a bare number goes in a round sign — putting anything else there would
 * present a value as a posted limit that nobody ever posted.
 */
export function postedLimit(maxspeed) {
  if (!maxspeed) return null;
  return /^\d+$/.test(String(maxspeed).trim()) ? String(maxspeed).trim() : null;
}

const dateLabel = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return dagMaand(d);
};

const SOURCE_NAMES = {
  "extern:osm": "OpenStreetMap",
  "extern:ndw": "NDW",
};

const longDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("nl-NL", { day: "numeric", month: "long" });
};

/**
 * How much longer this runs, in whole days, or null when there is no end date.
 *
 * Rounded up: a job ending later today has "nog 1 dag" to go, not zero. And a
 * date already past returns null rather than a negative — the feed has not
 * caught up yet, and "nog -3 dagen" is worse than saying nothing.
 */
export function daysLeft(endIso, from = new Date()) {
  if (!endIso) return null;
  const end = new Date(endIso);
  if (Number.isNaN(end.getTime())) return null;
  const days = Math.ceil((end - from) / 86400000);
  return days > 0 ? days : null;
}

/** A ring gauge. Purely decorative around a number that speaks for itself. */
export function Ring({ percent, colour, size = 54, label = "zeker" }) {
  const r = size / 2 - 4;
  const c = 2 * Math.PI * r;
  return (
    <div className="idr-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#1f2932" strokeWidth="3.5" />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={colour} strokeWidth="3.5"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - percent / 100)}
        />
      </svg>
      <span className="idr-ring-val">
        <b>{percent}%</b>
        <i>{label}</i>
      </span>
    </div>
  );
}

/**
 * "gisteren 18:20" — when this was last confirmed still standing.
 *
 * The clock matters here in a way it does not elsewhere on this screen: for
 * something that has stood for weeks, the only open question is whether it is
 * still there, and "gisteren 18:20" answers it more precisely than "1 dag
 * geleden" while taking the same space.
 */
export function lastSeenLabel(iso, now = new Date()) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const time = d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
  const midnight = new Date(now);
  midnight.setHours(0, 0, 0, 0);
  const daysBack = Math.floor((midnight - d) / 86400000) + 1;
  if (daysBack <= 0) return `vandaag ${time}`;
  if (daysBack === 1) return `gisteren ${time}`;
  if (daysBack < 7) return `${daysBack} dagen geleden`;
  return dagMaand(d);
}

/** "24 dagen" / "1 dag", or null. */
export function dayCount(days) {
  if (days === null || days === undefined) return null;
  return `${days} ${days === 1 ? "dag" : "dagen"}`;
}

/**
 * One cell per day, oldest on the left, today on the right.
 *
 * The heatmap above it has the hour of the day on its x-axis, which folds every
 * Thursday of the period into one row. That answers "when does it happen here",
 * a good question for a check that lasts two hours and the wrong one for a
 * camera that has stood for three weeks. Here the axis is a date, so standing
 * every day and appearing every other week look different.
 */
function DayStrip({ strip }) {
  const start = new Date();
  start.setDate(start.getDate() - (strip.span - 1));
  const mid = new Date();
  mid.setDate(mid.getDate() - Math.floor((strip.span - 1) / 2));
  const short = dagMaand;
  return (
    <>
      <div className="idr-strip" style={{ gridTemplateColumns: `repeat(${strip.span}, 1fr)` }}>
        {strip.cells.map((on, i) => <i key={i} className={on ? "on" : ""} />)}
      </div>
      <div className="idr-strip-ax">
        <span>{short(start)}</span><span>{short(mid)}</span><span>vandaag</span>
      </div>
      <div className="idr-strip-cap">
        {/* `since` en niet `span`: de strook tekent dertig hokjes, maar de
            dagen waarop deze melding nog niet bestond horen niet in de noemer.
            "3 van de 30 dagen" boven iets van vier dagen oud leest als
            zesentwintig dagen stilte. Valt terug op `span` voor een oudere
            server. */}
        Bevestigd op <b>{strip.confirmed} van de {strip.since ?? strip.span} dagen</b>
        {/* The counterweight to that number: eleven of thirty reads as solid
            until you learn nine of them were consecutive. */}
        {strip.longest_gap > 0 && ` — nooit langer dan ${dayCount(strip.longest_gap)} stil`}
      </div>
    </>
  );
}

/**
 * "sinds 12 aug 20:51" — wanneer dit voor het eerst gemeld is.
 *
 * Dezelfde vorm als firstSeenLabel op Android, tot de grens van een week aan
 * toe: binnen de week met het uur erbij (een melding van gisteravond laat en
 * een van vanochtend vroeg lezen anders hetzelfde terwijl er twaalf uur tussen
 * zit), daarna alleen de datum met het jaar.
 */
function firstSeenLabel(iso) {
  if (!iso) return null;
  const seen = new Date(iso);
  if (Number.isNaN(seen.getTime())) return null;
  const dagen = Math.floor((Date.now() - seen.getTime()) / 86400000);
  // Woord voor woord hetzelfde als firstSeenLabel op Android. Dat was het niet:
  // die schreef "12 aug 20:51" en deze "12 aug, 18:51" — een komma erbij, uit
  // een tabel die we niet in de hand hadden. Zie maanden.js.
  const staart = dagen >= 0 && dagen < 7 ? uurMinuut(seen) : seen.getFullYear();
  return `sinds ${dagMaand(seen)} ${staart}`;
}

function ageLabel(minutes) {
  if (minutes === null || minutes === undefined) return null;
  if (minutes < 1) return "zojuist";
  if (minutes < 60) return `${minutes} min geleden`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} uur geleden`;
  return `${Math.round(hours / 24)} dagen geleden`;
}

/** Seven rows of twenty-four cells, scaled against the busiest hour. */
function Heatmap({ grid }) {
  const peak = Math.max(1, ...grid.flat());
  return (
    <>
      <div className="idr-hm">
        <div className="idr-hm-days">
          {DAY_LABELS.map((d) => <span key={d}>{d}</span>)}
        </div>
        <div className="idr-hm-grid">
          {grid.map((row, day) =>
            row.map((count, hour) => {
              const step = count === 0 ? 0 : 1 + Math.round((count / peak) * (HEAT_STEPS.length - 2));
              return (
                <i
                  key={`${day}-${hour}`}
                  style={{ background: HEAT_STEPS[step] }}
                  title={`${DAY_LABELS[day]} ${String(hour).padStart(2, "0")}:00 — ${count}`}
                />
              );
            })
          )}
        </div>
      </div>
      <div className="idr-hm-hours"><span>00</span><span>06</span><span>12</span><span>18</span></div>
      <div className="idr-hm-key">
        Weinig
        {HEAT_STEPS.map((c) => <i key={c} style={{ background: c }} />)}
        Veel
      </div>
    </>
  );
}

export default function IntelligenceDetail({ report, userId, distanceKm, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [window_, setWindow] = useState("30d");
  const [failed, setFailed] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [reportedGone, setReportedGone] = useState(false);

  useEffect(() => {
    if (!report?.id) return;
    let live = true;
    setFailed(false);
    axios
      .get(`${API}/reports/${report.id}/intelligence`, {
        // Wie er kijkt, zodat de knoppen weten wat jij hier al deed. Zonder dit
        // hield alleen component-state dat bij: sloot je het scherm, dan stond
        // er weer "Vrijgeven", en een tweede druk leverde "Je hebt dit al
        // gemeld" op dat nergens terechtkwam.
        params: { window: window_, ...(userId ? { user_id: userId } : {}) },
      })
      .then((res) => {
        if (!live) return;
        setData(res.data);
        // De server wint van wat dit scherm zelf onthouden heeft: hij weet het
        // ook nog na een herstart, en ook als je het vanaf een ander toestel
        // doorgaf.
        if (res.data.signalled_by_me) setReportedGone(true);
        if (res.data.confirmed_by_me) setConfirmed(true);
      })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [report?.id, window_, userId]);

  const confirm = useCallback(async () => {
    if (confirmed) return;
    try {
      await axios.post(`${API}/report/confirm`, {
        report_id: report.id, user_id: userId, action: "confirm",
      });
      setConfirmed(true);
      onChanged && onChanged();
    } catch (e) {
      console.error("Bevestigen mislukt:", e);
    }
  }, [confirmed, report, userId, onChanged]);

  const signalGone = useCallback(async () => {
    if (reportedGone) return;
    try {
      await axios.post(`${API}/report/gone-signal`, { report_id: report.id, user_id: userId });
      setReportedGone(true);
      onChanged && onChanged();
    } catch (e) {
      console.error("Signaal mislukt:", e);
    }
  }, [reportedGone, report, userId, onChanged]);

  // Sharing moved here from the board, where it was one of three buttons under
  // every row. Telegram's own share sheet does the work, so there is no dialogue
  // of our own to step through first.
  const share = useCallback(() => {
    if (!report) return;
    const control = report.control_name || report.control_type || "controle";
    const lines = [
      `${report.authority_name} · ${control}`,
      report.address || report.city,
      report.confirmations > 0 ? `${report.confirmations}× bevestigd` : null,
      "",
      "via MeldRadar",
    ].filter((l) => l !== null && l !== undefined);
    const url = `https://t.me/share/url?url=https://t.me/meldradar_nl&text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank");
  }, [report]);

  if (!report) return null;

  const mark = authorityMark(report.authority, report.authority_name);
  /* "Mobiele flitser · Algemeen" was two extra words that say nothing, and the
     mark beside it already says which service — so a camera read "FLIT ·
     Mobiele flitser · Algemeen", the same thing three times. Same rule as
     cardTitle() on the board and titleOf() in the native app: drop the control
     type when it is generic, and let the mark carry the service. */
  const title = GENERIC_CONTROL.has(report.control_name || "")
    ? report.authority_name
    : `${report.authority_name} · ${report.control_name}`;
  const age = ageLabel(data?.age_minutes);
  // Read from the report, not from `data`: the action bar renders before the
  // background request lands, and a confirm button that appears for a second
  // and then vanishes is worse than one that was never there.
  const isOfficial = !!report.is_official;
  // Direction only means something for a camera: road works do not face a way.
  const isCamera = report.authority === "vaste_flitser";
  /* Something that stays put — a mobile camera, a road closure — asks a
     different question than a check that lasts two hours. "Wanneer gebeurt het
     hier" is answered by the weekly clock; "staat hij er nog, en hoe lang al"
     is not, and that is the only question anyone has about a camera that has
     been on the same verge for three weeks. The backend decides which of the
     two this is; the screen only forks on it. */
  const isPersistent = !isOfficial && data?.lifecycle_type === "persistent";
  /* Wat vastgeschroefd staat, waar de melding ook vandaan komt.

     Van de weg: "vaste flitsers hebben geen bevestigingen nodig, wel de optie
     om aan te geven dat hij er niet meer is." Dat is één regel voor een paal
     uit OpenStreetMap en een paal die iemand zelf meldde — het is dezelfde
     paal. Bij een mobiele flitser is de vraag "stond daar zojuist echt
     iemand", en die beantwoord je met meer waarnemers; bij een paal is de
     vraag "staat daar een paal", en die kan de volgende voorbijganger zelf
     zien. */
  /* Een paal die nog bevestigd moet worden, krijgt de gewone twee knoppen:
     bevestigen is daar precies wat er nodig is. Pas als hij er eenmaal staat,
     is bevestigen zinloos en blijft alleen "staat er niet" over. */
  const vastgeschroefd =
    (isOfficial || !!data?.infrastructure) && !data?.needs_confirmation;

  return (
    <div className="idr" data-testid="intelligence-detail">
      <div className="idr-nav">
        <button className="idr-back" onClick={onClose} aria-label="Terug">‹</button>
        <button className="idr-share" onClick={share} aria-label="Deel melding"
                data-testid="detail-share">↗</button>
      </div>

      <div className="idr-head">
        <div className="idr-titlerow">
          <span className={`idr-mark m-${mark.tone}`}>{mark.text}</span>
          <span className="idr-title">{title}</span>
        </div>
        <div className="idr-subrow">
          <div className="idr-sub">
            <span className="idr-road">{report.address || report.city}</span>
            {report.lat != null && (
              <span className="idr-coord">⌖ {report.lat.toFixed(4)}, {report.lon.toFixed(4)}</span>
            )}
          </div>
          {distanceKm != null && (
            <div className="idr-dist">
              <b>{distanceKm.toFixed(1)}<span> km</span></b>
              <i>vanaf jou</i>
            </div>
          )}
        </div>
        {/* The one fact that carries the screen for something that stays put,
            and the one nobody else can tell you. It goes in the header rather
            than in a panel because it is the answer, not a detail. */}
        {isPersistent && data.standing_days != null && (
          <div className="idr-headline">
            <span className="idr-pip" />Staat hier al {dayCount(data.standing_days)}
            {/* De datum erachter, want een getal dat uit een klok komt kun je
                niet nakijken. Zo gevraagd van de weg: "de dagen kloppen niet,
                deze hoort meer dan vijf dagen te staan." */}
            {firstSeenLabel(data.first_seen_at) && (
              <em className="idr-since">{firstSeenLabel(data.first_seen_at)}</em>
            )}
          </div>
        )}
      </div>

      <div className="idr-scroll">
        {failed && <div className="idr-panel idr-muted">Achtergrondinformatie kon niet geladen worden.</div>}

        {/* An official item is a different kind of thing, and the community
            screen lies about it. A fixed camera is not 79% certain — it is
            there. Nobody will ever confirm it. Its "historical pattern" is
            "always". So the middle of this screen is replaced by the facts a
            driver can act on, plus what the community reported around it. */}
        {data && data.is_official && (
          <>
            {(postedLimit(data.maxspeed) || isCamera) && (
              <div className="idr-panel idr-facts">
                {postedLimit(data.maxspeed) && (
                  <div className="idr-limit">
                    <span className="idr-limit-sign">{postedLimit(data.maxspeed)}</span>
                    <span className="idr-limit-label">limiet hier</span>
                  </div>
                )}
                {/* Shown for a camera even when the direction is unknown, and
                    especially then: not knowing has a consequence the driver
                    can feel, because the app will warn regardless of which way
                    they are going. Silence would hide that. */}
                {isCamera && (
                  <div className="idr-facing">
                    {data.heading != null ? (
                      <>
                        <b>Meet richting {compassPoint(data.heading)}</b>
                        <i>{Math.round(data.heading)}° · rijd je de andere kant op, dan geldt hij niet voor jou</i>
                      </>
                    ) : (
                      <>
                        <b>Richting onbekend</b>
                        <i>je krijgt daarom altijd een waarschuwing, ongeacht je rijrichting</i>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Road works: the two questions a driver actually has — how long,
                and until when. Both were already in the feed and stored on the
                document; the end date only ever reached anyone glued onto the
                address as "· t/m 14-08", and the description was parsed and
                then dropped. */}
            {(longDate(data.window_end) || data.description) && (
              <div className="idr-panel">
                {longDate(data.window_end) && (
                  <div className="idr-period">
                    <b>
                      {longDate(data.window_start)
                        ? `Van ${longDate(data.window_start)} t/m ${longDate(data.window_end)}`
                        : `Tot en met ${longDate(data.window_end)}`}
                    </b>
                    {daysLeft(data.window_end) && (
                      <i>nog {daysLeft(data.window_end)} {daysLeft(data.window_end) === 1 ? "dag" : "dagen"}</i>
                    )}
                  </div>
                )}
                {data.description && (
                  <p className="idr-description">{data.description}</p>
                )}
              </div>
            )}

            {/* What "4 locaties" actually stands for.
                ------------------------------------------------------------
                NDW publishes one project as several per-address records and
                OSM puts one camera node per lane, so the board merges them
                into a single row. The count survived the merge and the members
                did not: a card reading "4 locaties" opened onto one address
                and one end date, which is a promise the screen could not keep.

                Nearest first, the order you reach them. Each with its own end
                date, because that is the whole reason they are separate
                records — one stretch of a corridor can finish weeks before the
                next. */}
            {(report.group_items || []).length > 1 && (
              <div className="idr-panel">
                <div className="idr-panel-h">
                  <h3>{report.group_count} locaties</h3>
                </div>
                <ul className="idr-locs">
                  {report.group_items.map((g, i) => (
                    <li key={`${g.address || i}-${i}`}>
                      <span className="idr-loc-name">{g.address || "Onbekend"}</span>
                      {longDate(g.window_end) && (
                        <span className="idr-loc-until">t/m {longDate(g.window_end)}</span>
                      )}
                    </li>
                  ))}
                </ul>
                {report.group_count > report.group_items.length && (
                  <p className="idr-loc-more">
                    en nog {report.group_count - report.group_items.length} andere
                  </p>
                )}
              </div>
            )}

            {/* The distinguishing bit. Not invented statistics about a pole —
                what your own people reported on this spot. A camera is static;
                what happens around it is not. */}
            {data.nearby && data.nearby.count > 0 && (
              <div className="idr-panel">
                <div className="idr-panel-h">
                  <h3>In de buurt</h3>
                  <div className="idr-segs">
                    {WINDOWS.map((w) => (
                      <button
                        key={w}
                        className={`idr-seg ${w === data.window ? "on" : ""}`}
                        onClick={() => setWindow(w)}
                      >{w}</button>
                    ))}
                  </div>
                </div>
                <p className="idr-nearby-line">
                  {data.nearby.count} {data.nearby.count === 1 ? "controle" : "controles"} gemeld
                  binnen 1 km in {data.nearby.days} dagen
                </p>
                <Heatmap grid={data.heatmap} />
                {data.pattern?.days?.length > 0 && (
                  <div className="idr-prow"><span className="idr-pi">▤</span>{data.pattern.days.join(", ")}</div>
                )}
                {data.pattern?.hours && (
                  <div className="idr-prow"><span className="idr-pi">◷</span>{data.pattern.hours.label}</div>
                )}
              </div>
            )}

            {/* Where this came from, and how fresh. Accountability for data we
                did not gather ourselves, and it exposes a stalled sync. */}
            <div className="idr-source">
              {SOURCE_NAMES[data.source] || "Officiële bron"}
              {dateLabel(data.last_synced_at) && ` · laatst gecontroleerd ${dateLabel(data.last_synced_at)}`}
            </div>
          </>
        )}

        {/* ---------- community, and it stays put ----------
            Three numbers that are literally what they say, in place of a
            reliability percentage built on a freshness model. That model is
            right for a check that lasts two hours: no confirmation in a while
            genuinely means it is probably over. For something standing for
            weeks it measures how busy the reporting is, not whether the thing
            is there, and it sinks while the camera stays put. */}
        {data && isPersistent && (
          <>
            <div className="idr-kpis">
              {data.standing_days != null && (
                <div className="idr-kpi good">
                  <b>{data.standing_days}<em>d</em></b><span>staat hier</span>
                </div>
              )}
              {/* People, not taps: how many separate human beings have said it
                  is there. "3× bevestigd" counts the same person's second look
                  the same as a stranger's first. */}
              {data.people > 0 && (
                <div className="idr-kpi">
                  <b>{data.people}<em>×</em></b><span>{data.people === 1 ? "persoon" : "mensen"}</span>
                </div>
              )}
              {lastSeenLabel(data.last_confirmed_at) && (
                <div className="idr-kpi">
                  <b className="sm">{lastSeenLabel(data.last_confirmed_at)}</b>
                  <span>laatst bevestigd</span>
                </div>
              )}
            </div>

            {data.day_strip && (
              <div className="idr-panel">
                <div className="idr-panel-h">
                  <h3>Staat hij er consequent?</h3>
                  <span className="idr-scope">{data.day_strip.span} dagen</span>
                </div>
                <DayStrip strip={data.day_strip} />
              </div>
            )}

            {/* Which way it measures. Shown for anything persistent, not just a
                fixed camera: a mobile camera on a verge faces one direction too,
                and not knowing has a consequence the driver can feel — we warn
                both ways. */}
            {data.heading != null && (
              <div className="idr-panel idr-facts">
                <div className="idr-facing">
                  <b>Meet richting {compassPoint(data.heading)}</b>
                  <i>{Math.round(data.heading)}° · rijd je de andere kant op, dan geldt hij niet voor jou</i>
                </div>
              </div>
            )}

            {/* The only block on this screen nobody else can produce. A camera's
                own page can say where it is; it cannot say this is the fifth
                time one has stood here. It exists purely because people report. */}
            {data.place && (
              <div className="idr-panel">
                <div className="idr-panel-h">
                  <h3>Deze plek</h3>
                  <span className="idr-scope">binnen 150 m</span>
                </div>
                <div className="idr-place">
                  <div className="idr-place-row">
                    <b>{data.place.count}×</b>
                    <span>stond hier eerder een controle</span>
                  </div>
                  <div className="idr-place-row">
                    <b>{dayCount(data.place.typical_days)}</b>
                    <span>bleef die gemiddeld staan</span>
                  </div>
                  <div className="idr-place-row">
                    <b>{dayCount(data.place.longest_days)}</b>
                    <span>langste periode</span>
                  </div>
                </div>
              </div>
            )}

            {/* Hoeveel mensen al zeiden dat hij weg is, en hoeveel er nodig
                zijn.

                Dit stond alleen in de tak voor tijdelijke controles, en juist
                een paal is het geval waar het om gaat: die staat weken en
                wordt wél vrijgegeven. Van de weg gemeld met drie
                schermafdrukken naast elkaar — Telegram zei "2× vrijgegeven",
                dit scherm zei er niets over en zette er een groen bolletje bij.

                Boven de drempel valt de noemer weg; dan staat de status het
                zelf al. Zie leeftijd.js: vrijgegevenLabel. */}
            {vrijgegevenLabel(data.gone_signals_count, data.gone_signals_needed) && (
              <div className="idr-gone">
                <span className="idr-gi">◉</span>
                <span>{vrijgegevenLabel(data.gone_signals_count, data.gone_signals_needed)}</span>
              </div>
            )}

            {/* Wat de knop eronder doet, in woorden. "Tik dan links" stond
                hier, en dat klopte toen er twee knoppen stonden; onder een paal
                staat er nu één over de volle breedte. */}
            <div className="idr-insight">
              <span className="idr-ii">◈</span>
              <span>
                {data?.needs_confirmation
                  ? "Nog niet bevestigd. Een paal blijft staan zodra iemand anders hem ook ziet; gebeurt dat binnen een week niet, dan verdwijnt hij vanzelf."
                  : vastgeschroefd
                    ? "Hoeft niet bevestigd te worden — hij staat er of hij staat er niet. Zie je hem niet meer, gebruik dan de knop onderaan."
                    : "Blijft staan tot meerdere mensen hem vrijgeven. Zie je hem niet meer, gebruik dan de knop links."}
              </span>
            </div>
          </>
        )}

        {data && !data.is_official && !isPersistent && (
          <>
            <div className="idr-kpis">
              <div className="idr-kpi good">
                <b>{data.reliability}<em>%</em></b><span>zekerheid</span>
              </div>
              {/* Duration is absent until enough controls here have actually
                  finished — see report_intelligence.duration_spread. */}
              {data.duration && (
                <div className="idr-kpi">
                  <b>{data.duration.label}</b><span>verwachte duur</span>
                </div>
              )}
              <div className="idr-kpi">
                <b>{data.confirmations}<em>×</em></b><span>bevestigd</span>
              </div>
            </div>

            <div className="idr-panel">
              <div className="idr-panel-h">
                <h3>Historisch patroon</h3>
                <div className="idr-segs">
                  {WINDOWS.map((w) => (
                    <button
                      key={w}
                      className={`idr-seg ${w === data.window ? "on" : ""}`}
                      onClick={() => setWindow(w)}
                    >{w}</button>
                  ))}
                </div>
              </div>
              <Heatmap grid={data.heatmap} />
            </div>

            {data.pattern && (
              <div className="idr-panel">
                <div className="idr-panel-h"><h3>Het gebeurt hier vooral</h3></div>
                {data.pattern.days?.length > 0 && (
                  <div className="idr-prow"><span className="idr-pi">▤</span>{data.pattern.days.join(", ")}</div>
                )}
                {data.pattern.hours && (
                  <div className="idr-prow"><span className="idr-pi">◷</span>{data.pattern.hours.label}</div>
                )}
                {data.pattern.control_type && (
                  <div className="idr-prow"><span className="idr-pi">▥</span>{data.pattern.control_type}</div>
                )}
              </div>
            )}

            <div className="idr-panel">
              <div className="idr-panel-h">
                <h3>Live situatie</h3>
                <span className="idr-live-state">
                  {/* Dezelfde drie woorden als op het bord. "Mogelijk
                      beëindigd" over een camera die vrijgegeven is klopt niet:
                      een controle houdt op, een camera wordt weggehaald. */}
                  {data.lifecycle_status === "active"
                    ? "Actieve melding"
                    : data.lifecycle_status === "possibly_removed"
                      ? "Mogelijk vrijgegeven"
                      : "Mogelijk beëindigd"}
                </span>
              </div>
              <div className="idr-liverow">
                <span className="idr-pulse" />
                {age && <span className="idr-fresh">{age}</span>}
                <span className="idr-muted-inline">laatste update</span>
              </div>
              {/* Hoeveel mensen al gezegd hebben dat hij weg is. Het getal
                  stond op de bordrij en niet op het scherm waar de knop staat,
                  precies waar je het wilt weten voordat je hem indrukt. */}
              {vrijgegevenLabel(data.gone_signals_count, data.gone_signals_needed) && (
                <div className="idr-liverow">
                  <span className="idr-muted-inline">
                    {vrijgegevenLabel(data.gone_signals_count, data.gone_signals_needed)}
                  </span>
                </div>
              )}
            </div>

            {/* A frequency, never a forecast. The count it rests on is shown
                next to it on purpose: a bare percentage invites the reader to
                imagine a model that does not exist. */}
            {data.prediction && (
              <div className="idr-predict">
                <div className="idr-predict-l">
                  <div className="idr-predict-label">Patroon op dit moment</div>
                  <div className="idr-predict-main">
                    {data.prediction.hits} van de laatste {data.prediction.sample} keer
                  </div>
                  <div className="idr-predict-sub">
                    {data.prediction.weekday} {data.prediction.hours_label} · hier gemeld
                  </div>
                </div>
                <Ring percent={data.prediction.percent} colour="#a855f7" label="keer" />
              </div>
            )}

            {data.insight && (
              <div className="idr-insight"><span className="idr-ii">◈</span><span>{data.insight}</span></div>
            )}
          </>
        )}

        {!data && !failed && <div className="idr-panel idr-muted">Laden…</div>}
      </div>

      {/* The two halves of the only question anyone has about a report: is it
          still there, or not.

          "Navigeren" used to hold the left slot, opening Google Maps with this
          spot as the destination — the most prominent place on the screen given
          to the one thing nobody does. Nobody navigates *to* a speed camera, a
          police check or a road closure; those are things you drive around.
          Releasing took its place, and no longer hangs below the bar as a
          separate line.

          Confirming and releasing are community mechanics: they only mean
          something for something a person saw.

          Een officieel item kreeg daarom helemaal geen balk, met als reden: een
          vaste flitser stopt niet met bestaan, en de volgende synchronisatie
          zet hem toch terug. Het tweede was waar en is gerepareerd (zie
          external_data_service.NIET_VRIJGEGEVEN); het eerste is dat niet. Van
          de weg: "sommige vaste flitsers waren weg." Een paal wordt weggehaald
          en OpenStreetMap loopt er maanden achteraan, en de enige die dat weet
          is degene die er net langs reed.

          Dus wel de linkerhelft en niet de rechter. "Staat er niet" is echte
          informatie die alleen van de weg kan komen; bevestigen is dat niet —
          een OSM-camera verloopt nergens, dus een duim omhoog verandert niets
          en zou alleen maar suggereren van wel. */}
      {vastgeschroefd ? (
        <div className="idr-actions">
          <button className="idr-btn ghost wide" onClick={signalGone} disabled={reportedGone}>
            {reportedGone ? "Doorgegeven" : "Staat er niet (meer)"}
          </button>
        </div>
      ) : (
        <div className="idr-actions">
          <button className="idr-btn ghost" onClick={signalGone} disabled={reportedGone}>
            {reportedGone ? "Doorgegeven" : "Vrijgeven"}
          </button>
          <button className="idr-btn primary" onClick={confirm} disabled={confirmed}>
            {confirmed ? "✓ Bevestigd" : "👍 Melding bevestigen"}
          </button>
        </div>
      )}
    </div>
  );
}
